// The SQL that the site sends to kinedb. This module builds text only; db.js
// sends it.
//
// Each name that goes into a statement must be a bare identifier, or a dotted
// path of them in a WHERE. The search box accepts a JSON query, so a name can
// come from the user, and a name that is not an identifier stops here.
//
// Rules that the measurements on :4902 gave (cvrweb CLAUDE.md, and 2026-09-13):
// - A name search is an equality on `_searchnames`, the array of name tokens.
//   It is an index lookup. `_searchname ILIKE '%x%'` took 133 663 000 us.
// - A DESC sort is `DESC NULLS LAST`. The kinedb default puts NULL first, and
//   "Ophørt" descending then showed only active companies.
// - `IN (...)` and an OR of equalities each took ~29.5 s on the cvrperson
//   index, where `=` took 144 us. Look up many ids with one `=` for each id.

const IDENT = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function ident(name, what = 'identifier') {
  if (typeof name !== 'string' || !IDENT.test(name)) {
    throw new Error(`${what} '${name}' is not a bare identifier`);
  }
  return name;
}

function fieldPath(name) {
  if (typeof name !== 'string') return ident(name, 'field');
  for (const segment of name.split('.')) ident(segment, 'field');
  return name;
}

/** A JS value as a SQL literal. */
export function quote(value) {
  if (value === null || value === undefined) return 'NULL';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return `'${String(value).replace(/'/g, "''")}'`;
}

export function eq(field, value) {
  return `${fieldPath(field)} = ${quote(value)}`;
}

export function and(...parts) {
  return parts.filter((part) => part && part !== '').join(' AND ');
}

/** A name search: each whole token must match. `$text` also matches whole tokens. */
export function textWhere(search) {
  const tokens = String(search ?? '').toLowerCase().split(/\s+/).filter((t) => t !== '');
  return and(...tokens.map((token) => eq('_searchnames', token)));
}

export function select({ from, fields, where, order, limit, offset } = {}) {
  const columns = fields && fields.length > 0 ? fields.map((f) => ident(f, 'field')).join(', ') : '*';
  let sql = `SELECT ${columns} FROM ${ident(from, 'collection')}`;
  if (where) sql += ` WHERE ${where}`;
  if (order?.field) {
    sql += ` ORDER BY ${ident(order.field, 'order field')} ${order.desc ? 'DESC NULLS LAST' : 'ASC'}`;
  }
  if (Number(limit) > 0) sql += ` LIMIT ${Math.floor(Number(limit))}`;
  if (Number(offset) > 0) sql += ` OFFSET ${Math.floor(Number(offset))}`;
  return sql;
}

/** `UPSERT INTO <into> ON (<on>) {json}, …` — insert-or-replace keyed on body fields. */
export function upsert({ into, on, docs } = {}) {
  if (!Array.isArray(docs) || docs.length === 0) throw new Error('upsert needs at least one document');
  const keys = (on ?? []).map((field) => ident(field, 'key field'));
  if (keys.length === 0) throw new Error('upsert needs at least one key field');
  return `UPSERT INTO ${ident(into, 'collection')} ON (${keys.join(', ')}) ${docs.map((doc) => JSON.stringify(doc)).join(', ')}`;
}

export function count({ from, where } = {}) {
  const sql = `SELECT COUNT(*) FROM ${ident(from, 'collection')}`;
  return where ? `${sql} WHERE ${where}` : sql;
}

function fieldCondition(field, value) {
  const col = fieldPath(field);
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return `${col} = ${quote(value)}`;
  const parts = [];
  for (const [op, operand] of Object.entries(value)) {
    switch (op) {
      case '$eq': parts.push(`${col} = ${quote(operand)}`); break;
      case '$ne': parts.push(`${col} != ${quote(operand)}`); break;
      case '$gt': parts.push(`${col} > ${quote(operand)}`); break;
      case '$gte': parts.push(`${col} >= ${quote(operand)}`); break;
      case '$lt': parts.push(`${col} < ${quote(operand)}`); break;
      case '$lte': parts.push(`${col} <= ${quote(operand)}`); break;
      case '$in': parts.push(`${col} IN (${(operand || []).map(quote).join(', ')})`); break;
      case '$nin': parts.push(`${col} NOT IN (${(operand || []).map(quote).join(', ')})`); break;
      case '$regex':
        throw new Error('$regex needs a full scan of the collection. Search for whole words, or for a CVR number.');
      default:
        throw new Error(`unsupported query operator '${op}'`);
    }
  }
  return parts.join(' AND ');
}

/** A MongoDB-shaped query object -> the body of a WHERE, without the word WHERE. */
export function mongoWhere(query) {
  if (!query || typeof query !== 'object') return '';
  const parts = [];
  for (const [key, value] of Object.entries(query)) {
    if (key === '$text') {
      parts.push(textWhere(value?.$search));
    } else if (key === '$or' || key === '$and') {
      const inner = (value || []).map(mongoWhere).filter((s) => s !== '');
      if (inner.length > 0) parts.push(`(${inner.join(key === '$or' ? ' OR ' : ' AND ')})`);
    } else {
      parts.push(fieldCondition(key, value));
    }
  }
  return and(...parts);
}
